import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Spin } from 'antd';
import { toast } from 'react-toastify';
import ContentLayout from '../../../../layout/ContentLayout';
import { Container } from '../../../../components/content';
import DeleteModal from '../../../../components/modals/DeleteModal';
import CategoryForm from '../../../../components/products/CategoryForm';
import categoryApi from '../../../../api/category-api';
import styles from '../../../../styles/mobileInfoPage.module.scss';

const mapCategoryToForm = (data) => ({
  slug: data.slug || '',
  menu_order: data.menu_order ?? '',
  image: data.image ? { src: data.image.src, name: data.image.name, alt: data.image.alt } : null,
  showOnHome: data.showOnHome || false,
  isPackages: data.isPackages || false,
  multiLanguage: {
    sr: { name: data.multiLanguage?.sr?.name || '', description: data.multiLanguage?.sr?.description || '' },
    hu: { name: data.multiLanguage?.hu?.name || '', description: data.multiLanguage?.hu?.description || '' },
    en: { name: data.multiLanguage?.en?.name || '', description: data.multiLanguage?.en?.description || '' },
  },
});

export default function IzmeniKategoriju() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [category, setCategory] = useState(null);
  const [initialForm, setInitialForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hideModalOpen, setHideModalOpen] = useState(false);
  const [hiding, setHiding] = useState(false);

  const loadCategory = useCallback(async () => {
    setLoading(true);
    const { data, success, message } = await categoryApi.getById(id);
    if (success && data) {
      setCategory(data);
      setInitialForm(mapCategoryToForm(data));
    } else {
      toast.error(message || 'Greška pri učitavanju kategorije');
      navigate('/proizvodi?tab=kategorije');
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    loadCategory();
  }, [loadCategory]);

  const handleSubmit = async (payload) => {
    setSaving(true);
    const { success, message } = await categoryApi.update(id, payload);
    if (success) {
      toast.success('Kategorija je uspešno izmenjena');
      navigate('/proizvodi?tab=kategorije');
    } else {
      toast.error(message || 'Greška pri izmeni kategorije');
    }
    setSaving(false);
  };

  const handleConfirmHide = async () => {
    setHiding(true);
    const { success, message } = await categoryApi.hide(id);
    if (success) {
      toast.success('Kategorija je uspešno sakrivena');
      setHideModalOpen(false);
      setCategory((prev) => ({ ...prev, isDeleted: true }));
    } else {
      toast.error(message || 'Greška pri sakrivanju kategorije');
    }
    setHiding(false);
  };

  const handleRestore = async () => {
    setHiding(true);
    const { success, message } = await categoryApi.restore(id);
    if (success) {
      toast.success('Kategorija je uspešno vraćena');
      setCategory((prev) => ({ ...prev, isDeleted: false }));
    } else {
      toast.error(message || 'Greška pri vraćanju kategorije');
    }
    setHiding(false);
  };

  if (loading || !initialForm) {
    return (
      <Container>
        <ContentLayout>
          <div className={styles.loadingContainer}>
            <Spin size="large" />
          </div>
        </ContentLayout>
      </Container>
    );
  }

  return (
    <Container>
      <Link to="/proizvodi?tab=kategorije" className="link_btn" style={{ marginBottom: '12px' }}>
        <img alt="back" src="/assets/icons/chevron.svg" />
        <span>Nazad</span>
      </Link>

      <ContentLayout pt={true}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <h2 className={styles.pageTitle} style={{ margin: 0 }}>
            Izmeni kategoriju
          </h2>
          {category?.isDeleted ? (
            <Button className="btn-tertiary" onClick={handleRestore} loading={hiding}>
              Vrati kategoriju
            </Button>
          ) : (
            <Button className="btn-primary red-button" onClick={() => setHideModalOpen(true)}>
              Sakrij kategoriju
            </Button>
          )}
        </div>

        <CategoryForm mode="edit" initialForm={initialForm} saving={saving} onSubmit={handleSubmit} />
      </ContentLayout>

      <DeleteModal
        visible={hideModalOpen}
        onDelete={handleConfirmHide}
        onCancel={() => setHideModalOpen(false)}
        loading={hiding}
        title="Sakrivanje kategorije"
        message={`Da li ste sigurni da želite da sakrijete kategoriju "${category?.name}"?`}
        buttonText="Sakrij kategoriju"
        cancelText="Odustani"
      />
    </Container>
  );
}
